// src/pages/About.tsx - PREMIUM MODERN CREATIVE VERSION

import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  Eye,
  Heart,
  TrendingUp,
  Users,
  Sparkles,
  Globe,
  Shield,
  Trophy,
  Flag,
  Flame,
  Star,
  Rocket,
  Calendar,
  MapPin,
  Medal,
  Crown,
  ChevronRight,
  ChevronLeft,
  Award,
  Zap,
  CheckCircle2,
  ArrowUpRight,
  Play,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { statsDisplay } from '@/data/mock/stats';

const About = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const heroRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [activeValue, setActiveValue] = useState(0);

  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const { scrollYProgress: timelineScroll } = useScroll({
    target: timelineRef,
    offset: ['start end', 'end start'],
  });

  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.3]);
  const heroOpacity = useTransform(heroScroll, [0, 0.5], [1, 0]);
  const timelineProgress = useTransform(timelineScroll, [0, 1], ['0%', '100%']);

  const navLabel = (ar: string, en: string) => (isRTL ? ar : en);

  // Arrow Component
  const DirectionalArrow = ({ className = '' }: { className?: string }) =>
    isRTL ? <ChevronLeft className={className} /> : <ChevronRight className={className} />;

  // Content
  const milestones = [
    {
      year: '2015',
      title: { ar: 'التأسيس والانطلاق', en: 'Foundation & Launch' },
      description: {
        ar: 'فكرة وُلدت بعد عدوان 2014 على يد أربعة أصدقاء، وانطلقت صيف 2015 على ملعب بسيط، لتستقبل 200 لاعب في موسمها الأول',
        en: 'An idea born after the 2014 war, founded by four friends and launched in summer 2015 on a simple field — welcoming 200 players in its first season',
      },
      icon: Rocket,
      color: 'from-yellow-500 to-orange-500',
      image: '/assets/images/s-blob-v1-IMAGE-ajQSFXLJsfw.jpeg',
    },
    {
      year: '2016',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2017',
      title: { ar: 'التوسع والريادة المجتمعية', en: 'Expansion & Community Leadership' },
      description: {
        ar: 'افتتاح فروع في محافظات غزة، وإطلاق أول برنامج رياضي للأطفال من مرضى السرطان في مبادرة فريدة عالميًا',
        en: 'Opening branches across Gaza\'s governorates, and launching the world\'s first sports program for children with cancer',
      },
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      image: '/assets/images/s-blob-v1-IMAGE-ggvobWIqLZc.jpeg',
    },
    {
      year: '2018',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2019',
      title: { ar: 'أول مشاركة دولية', en: 'First International Stage' },
      description: {
        ar: 'المشاركة في أول بطولة دولية، الدونستي كاب في إسبانيا، وانطلاق الاحتكاك العالمي',
        en: 'Participating in our first international tournament, the Donosti Cup in Spain — the start of global exposure',
      },
      icon: Globe,
      color: 'from-blue-500 to-cyan-500',
      image: '/assets/images/s-blob-v1-IMAGE-HiJ0_LRWIVo.jpeg',
    },
    {
      year: '2020',
      title: { ar: 'شراكة ريال مدريد وأفضل أكاديمية', en: 'Real Madrid & Best Academy' },
      description: {
        ar: 'شراكة استراتيجية مع مؤسسة ريال مدريد، ونيل لقب أفضل أكاديمية في فلسطين من المجلس الأعلى للشباب والرياضة',
        en: 'A strategic partnership with the Real Madrid Foundation, and being named the best academy in Palestine by the Higher Council for Youth & Sports',
      },
      icon: Star,
      color: 'from-green-500 to-emerald-500',
      image: '/assets/images/s-blob-v1-IMAGE-7bc95X8VQFY.jpeg',
    },
    {
      year: '2021',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2022',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2023',
      title: { ar: 'مجموعة رياضية متكاملة', en: 'An Integrated Sports Group' },
      description: {
        ar: 'تحوّل تشامبيونز إلى مجموعة رياضية متعددة الكيانات والتخصصات، وتبنّي التكنولوجيا الرياضية في التدريب',
        en: 'Champions evolves into a multi-entity sports group, adopting sports technology across its training programs',
      },
      icon: Trophy,
      color: 'from-amber-500 to-yellow-500',
      image: '/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg',
    },
    {
      year: '2024',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2025',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
    {
      year: '2026',
      title: { ar: 'قريبًا', en: 'Coming soon' },
      description: {
        ar: 'محطة سيتم الإعلان عن تفاصيلها قريبًا',
        en: 'A milestone to be announced soon',
      },
      icon: Calendar,
      color: 'from-slate-500 to-slate-400',
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
    },
  ];

  const values = [
    {
      icon: Rocket,
      title: { ar: 'الريادة', en: 'Pioneering' },
      description: {
        ar: 'نسعى لترك أثر يتجاوز حدود الرياضة عبر بناء منظومة متكاملة تُحدث تغييرًا مستدامًا في المجتمع',
        en: 'We strive to leave an impact beyond sport by building an integrated system that creates sustainable change in society',
      },
      image: '/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg',
      gradient: 'from-yellow-500/20 to-orange-500/20',
    },
    {
      icon: Award,
      title: { ar: 'التفوق', en: 'Excellence' },
      description: {
        ar: 'نؤمن بالتطوير المستمر واعتماد أساليب تدريب مبتكرة تمنح لاعبينا القدرة على المنافسة محليًا ودوليًا',
        en: 'We believe in continuous development and innovative training methods that empower our players to compete locally and internationally',
      },
      image: '/assets/images/s-blob-v1-IMAGE-g2oSy57XucA.jpeg',
      gradient: 'from-blue-500/20 to-cyan-500/20',
    },
    {
      icon: Trophy,
      title: { ar: 'المنافسة', en: 'Competition' },
      description: {
        ar: 'نعتبر المنافسة أداة للنمو؛ في الفوز نقرأ مكامن القوة، وفي الخسارة نحدد فرص التحسين، وفي كلتا الحالتين نرتقي',
        en: 'We see competition as a tool for growth: in victory we read our strengths, in defeat we identify what to improve, and in both we rise',
      },
      image: '/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg',
      gradient: 'from-amber-500/20 to-yellow-500/20',
    },
    {
      icon: Users,
      title: { ar: 'التعاون', en: 'Teamwork' },
      description: {
        ar: 'نحن أسرة واحدة، نعمل بروح الفريق، ونؤمن بأن النجاح يُبنى بالشراكة والتكامل',
        en: 'We are one family, working with team spirit, believing that success is built through partnership and integration',
      },
      image: '/assets/images/s-blob-v1-IMAGE-LIWinNKC_d4.jpeg',
      gradient: 'from-green-500/20 to-emerald-500/20',
    },
    {
      icon: Shield,
      title: { ar: 'الاحترام', en: 'Respect' },
      description: {
        ar: 'نحترم الإنسان أولًا، ونعزز ثقافة التقبل والانتماء باعتبارنا كيانًا موحدًا يجمعنا هدف مشترك',
        en: 'We respect the human being first, nurturing a culture of acceptance and belonging as one unified entity bound by a shared goal',
      },
      image: '/assets/images/s-blob-v1-IMAGE-IYhvf5Sdg_U.jpeg',
      gradient: 'from-pink-500/20 to-rose-500/20',
    },
  ];

  // Champions Group entities (شركات تشامبيونز)
  const entities = [
    {
      icon: Crown,
      title: { ar: 'أكاديمية تشامبيونز', en: 'Champions Academy' },
      description: {
        ar: 'حجر الأساس في المنظومة، تنتشر عبر خمسة فروع في قطاع غزة وتقدم برامج متعددة الرياضات تشمل كرة القدم، كرة السلة، تنس الطاولة، الكاراتيه، السباحة، الدبكة، الإسكواش والتنس الأرضي، وفق منهجيات حديثة ومعايير احترافية',
        en: 'The cornerstone of the system, spread across five branches in Gaza, offering multi-sport programs — football, basketball, table tennis, karate, swimming, dabke, squash and tennis — with modern methodologies and professional standards',
      },
    },
    {
      icon: Heart,
      title: { ar: 'النادي العائلي الرياضي الاجتماعي', en: 'The Social-Family Sports Club' },
      description: {
        ar: 'أول نموذج من نوعه في فلسطين يجمع بين الرياضة والبُعد الاجتماعي والأسري، ويخلق مساحة تحتضن العائلة حول قيم الرياضة ونمط حياة صحي',
        en: 'The first model of its kind in Palestine, combining sport with a social and family dimension — creating a space that gathers families around the values of sport and a healthy lifestyle',
      },
    },
    {
      icon: Zap,
      title: { ar: 'تشامبيونز جلوبال للتكنولوجيا الرياضية', en: 'Champions Global for Sports Technology' },
      description: {
        ar: 'ذراع متخصص في الرقمنة والتحليل الرياضي وتطوير الحلول التقنية في التدريب وقياس الأداء، بتوجه عالمي ينقل الخبرات والتقنيات الحديثة للرياضة الفلسطينية',
        en: 'A specialized arm for digitization, sports analytics and tech solutions in training and performance measurement, with a global outlook that brings modern expertise to Palestinian sport',
      },
    },
    {
      icon: Calendar,
      title: { ar: 'تشامبيونز بزنس للفعاليات', en: 'Champions Business for Events' },
      description: {
        ar: 'شركة لإدارة وتنظيم الفعاليات الرياضية والمبادرات المجتمعية، ودعم البرامج ذات الأثر التنموي والاجتماعي',
        en: 'A company for managing and organizing sports events and community initiatives, supporting programs with developmental and social impact',
      },
    },
    {
      icon: Trophy,
      title: { ar: 'نادي الأبطال الرياضي', en: 'Al-Abtal Sports Club' },
      description: {
        ar: 'كيان رياضي تنافسي يهدف إلى احتضان المواهب وصقلها ضمن بيئة احترافية داعمة للتميز',
        en: 'A competitive sports entity dedicated to nurturing and refining talent within a professional environment that supports excellence',
      },
    },
    {
      icon: Shield,
      title: { ar: 'نادي الجلاء الرياضي', en: 'Al-Jalaa Sports Club' },
      description: {
        ar: 'إطار شراكة وتعاون يعزز الحضور المؤسسي الرياضي ويدعم انتشار الرياضة التنافسية محليًا',
        en: 'A framework of partnership and cooperation that strengthens institutional sports presence and supports competitive sport locally',
      },
    },
  ];

  // Strategic partnerships (الشراكات الاستراتيجية)
  const partnerships = [
    {
      title: { ar: 'مؤسسة ريال مدريد', en: 'Real Madrid Foundation' },
      country: { ar: 'إسبانيا', en: 'Spain' },
      description: {
        ar: 'شراكة استراتيجية تُعد الأولى من نوعها في فلسطين، لنقل الخبرات التدريبية وفق منهجية عالمية وفتح آفاق احتكاك دولي للاعبين والكادر الفني',
        en: 'A strategic partnership — the first of its kind in Palestine — transferring coaching expertise through a world-class methodology and opening international exposure for players and staff',
      },
    },
    {
      title: { ar: 'شركة ريجينال سبورت', en: 'Regional Sport' },
      country: { ar: 'الإمارات', en: 'UAE' },
      description: {
        ar: 'متخصصة في تسويق اللاعبين وفتح فرص الاحتراف الخارجي، بما يعزز حضور المواهب الفلسطينية في الأسواق الرياضية الدولية',
        en: 'Specialized in player marketing and creating professional opportunities abroad, boosting Palestinian talent in international sports markets',
      },
    },
    {
      title: { ar: 'شركة نافس للرياضات والمعايشات', en: 'Nafs Sports' },
      country: { ar: 'مصر', en: 'Egypt' },
      description: {
        ar: 'تنظيم معسكرات تدريبية وبرامج احتكاك دولية تتيح للاعبين تجارب تنافسية خارجية',
        en: 'Organizing training camps and international exposure programs that give players competitive experiences abroad',
      },
    },
    {
      title: { ar: 'بطولة دونستي كاب', en: 'Donosti Cup' },
      country: { ar: 'إسبانيا', en: 'Spain' },
      description: {
        ar: 'مشاركة في بطولة دولية معتمدة للفئات السنية بمعايير احترافية تعزز الخبرة التنافسية العالمية',
        en: 'Participation in a recognized international youth tournament with professional standards that build global competitive experience',
      },
    },
    {
      title: { ar: 'مركز برشلونة للابتكار والتدريب الرياضي', en: 'Barcelona Innovation & Training Center' },
      country: { ar: 'إسبانيا', en: 'Spain' },
      description: {
        ar: 'تطوير الكادر الفني والإداري وفق أحدث معايير علوم الرياضة وتبني منهجيات تدريب متقدمة قائمة على التحليل العلمي والرقمي',
        en: 'Developing technical and administrative staff to the latest sports-science standards, adopting advanced methodologies based on scientific and digital analysis',
      },
    },
  ];

  return (
    <div className="min-h-screen">
      {/* ==================== HERO SECTION ==================== */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Parallax Background */}
        <motion.div
          style={{ scale: heroScale, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/assets/images/s-blob-v1-IMAGE-ajQSFXLJsfw.jpeg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background" />
        </motion.div>

        {/* Animated Gradient Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-champions-accent/20 rounded-full blur-3xl"
        />

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 text-center py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/30 backdrop-blur-xl mb-8"
            >
              <Crown className="h-5 w-5 text-primary" />
              <span className="text-sm font-bold text-primary">
                {navLabel('عن أكاديمية تشامبيونز', 'About Champions Academy')}
              </span>
              <Sparkles className="h-4 w-4 text-primary/70" />
            </motion.div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="gradient-text block"
              >
                {navLabel('نبني الأبطال', 'Building Champions')}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="gradient-text block"
              >
                {navLabel('نغيّر الحياة', 'Transforming Lives')}
              </motion.span>
            </h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              {navLabel(
                'منذ 2015، ونحن نشكّل مستقبل الرياضة في فلسطين من خلال التميز والالتزام والابتكار',
                'Since 2015, shaping the future of sports in Palestine through excellence, dedication, and innovation'
              )}
            </motion.p>

            {/* Stats Quick View */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-wrap justify-center gap-4 sm:gap-6"
            >
              {[
                { icon: Trophy, value: '30+', label: navLabel('إنجاز', 'Achievements') },
                { icon: Users, value: '5000+', label: navLabel('لاعب', 'Players') },
                { icon: MapPin, value: '5', label: navLabel('فروع', 'Branches') },
                { icon: Globe, value: '10+', label: navLabel('سنوات', 'Years') },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.05, y: -4 }}
                  className="group"
                >
                  <div className="px-6 py-4 rounded-2xl bg-card/80 backdrop-blur-md border border-border/50 hover:border-primary/50 transition-all">
                    <div className="flex items-center gap-3">
                      <stat.icon className="h-6 w-6 text-primary" />
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <div className="text-xs text-muted-foreground">{stat.label}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-16"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block"
              >
                <div className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center pt-2">
                  <motion.div
                    animate={{ y: [0, 12, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-1.5 h-1.5 bg-primary rounded-full"
                  />
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ==================== CHAMPIONS GROUP MEMBERSHIP ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(var(--primary-rgb),0.08)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(var(--accent-rgb),0.08)_0%,transparent_60%)]" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Badge */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/30 mb-8">
                <Award className="h-5 w-5 text-primary" />
                <span className="text-sm font-bold text-primary">
                  {navLabel('جزء من مجموعة تشامبيونز', 'Part of Champions Group')}
                </span>
                <Sparkles className="h-4 w-4 text-primary/70" />
              </div>

              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
                {navLabel('إرث من التميز', 'A Legacy of Excellence')}
              </h2>

              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                {navLabel(
                  'أكاديمية تشامبيونز هي عضو فخور في مجموعة تشامبيونز، المؤسسة الرياضية الرائدة في فلسطين',
                  'Champions Academy is a proud member of Champions Group, Palestine\'s leading sports organization'
                )}
              </p>
            </div>

            {/* Impact Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
              {[
                { icon: Crown, stat: '6', label: navLabel('كيانات رياضية', 'Sports Entities'), gradient: 'from-yellow-500/10 to-orange-500/10' },
                { icon: Shield, stat: '10+', label: navLabel('سنوات تميز', 'Years Excellence'), gradient: 'from-blue-500/10 to-cyan-500/10' },
                { icon: Globe, stat: '5', label: navLabel('شركاء دوليون', 'Global Partners'), gradient: 'from-green-500/10 to-emerald-500/10' },
                { icon: Trophy, stat: '30+', label: navLabel('إنجاز رياضي', 'Achievements'), gradient: 'from-purple-500/10 to-pink-500/10' },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className="relative bg-card border border-border rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className="inline-flex p-4 rounded-2xl bg-primary/10 border border-primary/20 mb-4"
                    >
                      <item.icon className="h-8 w-8 text-primary" />
                    </motion.div>
                    <div className="text-5xl font-bold mb-2 gradient-text">{item.stat}</div>
                    <div className="text-sm text-muted-foreground">{item.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== OUR STORY ==================== */}
      <section className="py-32 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-20"
            >
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Flag className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {navLabel('قصتنا', 'Our Story')}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
                {navLabel('من الرؤية إلى الواقع', 'From Vision to Reality')}
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Image Collage */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`relative ${isRTL ? 'lg:order-2' : ''}`}
              >
                <div className="relative">
                  {/* Main Image */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative rounded-3xl overflow-hidden shadow-2xl"
                  >
                    <img
                      src="/assets/images/s-blob-v1-IMAGE-HiJ0_LRWIVo.jpeg"
                      alt="Champions Story"
                      className="w-full h-[600px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />

                    {/* Year Badge */}
                    <div className={`absolute bottom-8 ${isRTL ? 'right-8 left-8' : 'left-8 right-8'} bg-card/90 backdrop-blur-xl p-6 rounded-2xl border border-border shadow-xl`}>
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-primary/10 border border-primary/20">
                          <Flame className="h-8 w-8 text-primary" />
                        </div>
                        <div className={isRTL ? 'text-right' : 'text-left'}>
                          <div className="text-3xl font-bold gradient-text">2015</div>
                          <div className="text-sm text-muted-foreground">
                            {navLabel('عام التأسيس', 'Year Founded')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating Stats */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className={`absolute -top-6 ${isRTL ? '-left-6' : '-right-6'} bg-card border border-border rounded-2xl p-6 shadow-2xl`}
                  >
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-primary" />
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <div className="text-2xl font-bold">5000+</div>
                        <div className="text-xs text-muted-foreground">{navLabel('لاعب', 'Players')}</div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Decorative element */}
                  <div className={`absolute -z-10 -bottom-8 ${isRTL ? '-right-8' : '-left-8'} w-full h-full bg-primary/10 rounded-3xl`} />
                </div>
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`space-y-8 ${isRTL ? 'lg:order-1' : ''}`}
              >
                <div className="space-y-6">
                  <p className={`text-muted-foreground text-lg leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                    {navLabel(
                      'في أعقاب العدوان على غزة عام 2014، ومن واقعٍ مثقل بالتحديات، وُلدت فكرة أكاديمية تشامبيونز على يد أربعة أصدقاء آمنوا بأن الرياضة أداة بناء وأمل. وفي صيف 2015 انطلقت بإمكانات متواضعة على ملعب بسيط، لكن برؤية واضحة وطموح كبير',
                      'In the aftermath of the 2014 war on Gaza, out of a reality heavy with challenges, the idea of Champions Academy was born — created by four friends who believed sport could be a tool for building and hope. In the summer of 2015 it launched with modest means on a simple field, but with a clear vision and great ambition'
                    )}
                  </p>

                  <p className={`text-muted-foreground text-lg leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                    {navLabel(
                      'لم تكن البداية سهلة، لكن الإيمان بالمشروع أثمر سريعًا؛ فاستقبلت الأكاديمية 200 لاعب في موسمها الأول، ثم امتدت أنشطتها لعشرة أشهر سنويًا وافتُتح أول فرع جديد، لتتحول المبادرة الشبابية إلى منظومة رياضية متكاملة',
                      'The start was not easy, but belief in the project soon bore fruit: the academy welcomed 200 players in its first season, then extended its activities to ten months a year and opened its first new branch — turning a youth initiative into a complete sports system'
                    )}
                  </p>
                </div>

                {/* Checkmarks */}
                <div className="space-y-4">
                  {[
                    navLabel('خمسة فروع في قطاع غزة', 'Five branches across the Gaza Strip'),
                    navLabel('أول أكاديمية في العالم لمرضى السرطان من الأطفال', 'World\'s first academy for child cancer patients'),
                    navLabel('شراكات دولية: ريال مدريد، برشلونة، دونستي كاب', 'International partners: Real Madrid, Barcelona, Donosti Cup'),
                  ].map((text, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <div className="flex-shrink-0 p-2 rounded-full bg-primary/10 border border-primary/20">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-foreground font-medium">{text}</span>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <Link to="/contact">
                    <Button size="lg" className="btn-glow group">
                      <span className="flex items-center gap-2">
                        {navLabel('تواصل معنا', 'Contact Us')}
                        <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* ==================== RESILIENCE THROUGH WAR ==================== */}
      <section className="py-32 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(var(--primary-rgb),0.08)_0%,transparent_60%)]" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Flame className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('صمود في وجه الحرب', 'Resilience Through War')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8">
              {navLabel('رسالة أقوى من الحرب', 'A Message Stronger Than War')}
            </h2>

            <p className={`text-muted-foreground text-lg md:text-xl leading-relaxed mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
              {navLabel(
                'لم تكن مسيرة تشامبيونز بمعزل عن الحروب المتكررة على غزة؛ فقد تضررت البنية التحتية بشكل كامل وتوقفت الأنشطة لفترات طويلة، وعانى أطفالنا من النزوح القسري وفقدان المنازل والضغوط النفسية الناتجة عن القصف والخوف المستمر. كما تضررت التجهيزات الرياضية، وتعقّدت فرص السفر والمشاركات الخارجية، وفقدنا عددًا من اللاعبين وعائلاتهم وكفاءات بارزة من منظومتنا.',
                'Champions\' journey has not been spared the repeated wars on Gaza. Infrastructure was completely damaged and activities halted for long periods; our children endured forced displacement, the loss of their homes, and the psychological strain of bombardment and constant fear. Sports equipment was damaged, travel and external participation became difficult, and we lost a number of players, their families, and prominent talents from our system.'
              )}
            </p>
            <p className={`text-muted-foreground text-lg md:text-xl leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
              {navLabel(
                'ورغم ذلك، أعدنا تفعيل الأنشطة فور توفّر الحد الأدنى من الأمان، لأن الرياضة عندنا ليست ترفًا في زمن الحرب، بل مساحة أمان وأداة لاستعادة التوازن وبناء الأمل في جيل يستحق مستقبلًا أفضل. أكدت الحرب أن الاستثمار الحقيقي يكمن في الإنسان؛ في قدرته على النهوض رغم الألم، ومواصلة الحلم رغم قسوة الظروف.',
                'Yet we resumed activities the moment a minimum of safety allowed — because for us sport is not a luxury in wartime, but a space of safety, a way to restore balance and build hope in a generation that deserves a better future. The war affirmed that the real investment is in the human being: in their ability to rise despite the pain, and to keep dreaming despite the harshest of circumstances.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ==================== TIMELINE ==================== */}
      <section ref={timelineRef} className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('رحلتنا', 'Our Journey')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('محطات من التاريخ', 'Milestones Through Time')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'رحلة استثنائية من الحلم إلى الواقع، من منشأة واحدة إلى 7 فروع تخدم آلاف الأبطال',
                'An exceptional journey from dream to reality, from one facility to 7 branches serving thousands of champions'
              )}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto relative">
            {/* Timeline Line */}
            <div className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} top-0 bottom-0 w-1 bg-border/30`} />
            <motion.div
              className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} top-0 w-1 bg-gradient-to-b from-primary via-champions-accent to-primary rounded-full shadow-lg shadow-primary/30`}
              style={{
                height: timelineProgress,
                transformOrigin: 'top',
              }}
            />

            {/* Milestones */}
            <div className="space-y-24">
              {milestones.map((milestone, idx) => {
                const Icon = milestone.icon;
                const isEven = idx % 2 === 0;

                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, delay: idx * 0.2 }}
                    className={`relative flex items-center ${
                      isRTL
                        ? isEven
                          ? 'md:flex-row-reverse'
                          : 'md:flex-row'
                        : isEven
                        ? 'md:flex-row'
                        : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Content Card */}
                    <div
                      className={`flex-1 ${
                        isRTL
                          ? isEven
                            ? 'md:pr-24 md:text-left'
                            : 'md:pl-24 md:text-right'
                          : isEven
                          ? 'md:pr-24 md:text-right'
                          : 'md:pl-24 md:text-left'
                      }`}
                    >
                      <motion.div
                        whileHover={{ scale: 1.02, y: -8 }}
                        className="inline-block w-full md:max-w-xl"
                      >
                        <div className="relative group">
                          <div
                            className={`absolute inset-0 bg-gradient-to-br ${milestone.color} opacity-20 rounded-3xl blur-xl group-hover:opacity-40 transition-opacity duration-500`}
                          />

                          <div className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                            {/* Image */}
                            <div className="relative h-64 overflow-hidden">
                              <img
                                src={milestone.image}
                                alt={milestone.title.en}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />

                              {/* Year Badge */}
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className={`absolute top-6 ${isRTL ? 'right-6' : 'left-6'} px-6 py-3 rounded-2xl bg-card/90 backdrop-blur-xl border border-border shadow-xl`}
                              >
                                <div className={`text-4xl font-bold bg-gradient-to-r ${milestone.color} bg-clip-text text-transparent`}>
                                  {milestone.year}
                                </div>
                              </motion.div>
                            </div>

                            {/* Content */}
                            <div className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                              <div className="flex items-center gap-4 mb-4">
                                <motion.div
                                  whileHover={{ scale: 1.2, rotate: 360 }}
                                  transition={{ duration: 0.6 }}
                                  className={`p-3 rounded-xl bg-gradient-to-br ${milestone.color} bg-opacity-10 border border-primary/20`}
                                >
                                  <Icon className="h-7 w-7 text-primary" />
                                </motion.div>
                                <h3 className="text-2xl font-bold">
                                  {milestone.title[isRTL ? 'ar' : 'en']}
                                </h3>
                              </div>

                              <p className="text-muted-foreground leading-relaxed text-lg">
                                {milestone.description[isRTL ? 'ar' : 'en']}
                              </p>
                            </div>

                            {/* Shine Effect */}
                            <div
                              className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                                isRTL
                                  ? 'translate-x-full group-hover:-translate-x-full'
                                  : '-translate-x-full group-hover:translate-x-full'
                              } transition-transform duration-1000`}
                            />
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Timeline Dot */}
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 20,
                        delay: idx * 0.2 + 0.3,
                      }}
                      className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} z-10`}
                    >
                      <motion.div
                        whileHover={{ scale: 1.4 }}
                        className={`relative w-10 h-10 bg-primary rounded-full border-4 border-background shadow-lg shadow-primary/50 ${
                          isRTL ? 'translate-x-1/2' : '-translate-x-1/2'
                        } cursor-pointer`}
                      >
                        <motion.div
                          animate={{
                            scale: [1, 1.8, 1],
                            opacity: [0.6, 0, 0.6],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                          className="absolute inset-0 bg-primary rounded-full"
                        />
                        <div className="absolute inset-1.5 bg-primary rounded-full" />
                      </motion.div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== VISION & MISSION ==================== */}
      <section className="py-32 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('رؤيتنا ورسالتنا', 'Vision & Mission')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('ما نطمح إليه', 'What Drives Us Forward')}
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              whileHover={{ y: -12 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />

              <div className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500 h-full">
                {/* Image Header */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src="/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg"
                    alt="Vision"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 10 }}
                    className={`absolute bottom-6 ${isRTL ? 'right-6' : 'left-6'} p-5 rounded-2xl bg-primary backdrop-blur-sm border border-primary/30 shadow-2xl`}
                  >
                    <Eye className="h-10 w-10 text-primary-foreground" />
                  </motion.div>
                </div>

                {/* Content */}
                <div className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <h3 className="text-3xl font-bold mb-6 group-hover:text-primary transition-colors">
                    {navLabel('الرؤية', 'Vision')}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {navLabel(
                      'أن نكون منظومة رياضية رائدة تُسهم في تطوير الأجيال على أسس رياضية وتربوية متكاملة، وترتقي بمستوى الرياضة المحلية، وتدعم المنتخبات الوطنية بلاعبين قادرين على تمثيل فلسطين في المحافل الدولية',
                      'To be a pioneering sports system that develops generations on integrated athletic and educational foundations, elevates local sport, and supports the national teams with players capable of representing Palestine on the international stage'
                    )}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ y: -12 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-champions-accent/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />

              <div className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-champions-accent/50 transition-all duration-500 h-full">
                {/* Image Header */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src="/assets/images/s-blob-v1-IMAGE-LIWinNKC_d4.jpeg"
                    alt="Mission"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

                  <motion.div
                    whileHover={{ scale: 1.15, rotate: -10 }}
                    className={`absolute bottom-6 ${isRTL ? 'right-6' : 'left-6'} p-5 rounded-2xl bg-champions-accent backdrop-blur-sm border border-champions-accent/30 shadow-2xl`}
                  >
                    <Target className="h-10 w-10 text-champions-accent-foreground" />
                  </motion.div>
                </div>

                {/* Content */}
                <div className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <h3 className="text-3xl font-bold mb-6 group-hover:text-champions-accent transition-colors">
                    {navLabel('الرسالة', 'Mission')}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {navLabel(
                      'توفير بيئة تدريبية متكاملة وآمنة عبر تطوير مستمر للمرافق والبنية التحتية، واعتماد منهجيات تدريب حديثة مواكبة للتكنولوجيا والعلوم الرياضية، بما يضمن بناء لاعب متكامل فنيًا وبدنيًا وتربويًا',
                      'To provide an integrated, safe training environment through continuous development of facilities and infrastructure, and to adopt modern training methodologies aligned with technology and sports science — building a player who is complete technically, physically, and educationally'
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== VALUES ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(var(--primary-rgb),0.06)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(var(--accent-rgb),0.06)_0%,transparent_60%)]" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Heart className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('قيمنا', 'Our Values')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('المبادئ التي نؤمن بها', 'The Principles We Stand For')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'القيم الجوهرية التي تشكل هويتنا وتوجه قراراتنا في كل خطوة نخطوها',
                'Core values that shape our identity and guide our decisions in every step we take'
              )}
            </p>
          </motion.div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8, delay: idx * 0.15 }}
                whileHover={{ y: -12 }}
                onHoverStart={() => setActiveValue(idx)}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`} />

                <div className="relative h-full bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  {/* Image */}
                  <div className="relative h-80 overflow-hidden">
                    <motion.img
                      src={value.image}
                      alt={value.title.en}
                      className="w-full h-full object-cover"
                      animate={{
                        scale: activeValue === idx ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.7 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />

                    {/* Icon Badge */}
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`absolute top-6 ${isRTL ? 'right-6' : 'left-6'} p-5 rounded-2xl bg-card/90 backdrop-blur-xl border border-primary/30 shadow-2xl`}
                    >
                      <value.icon className="h-10 w-10 text-primary" />
                    </motion.div>

                    {/* Floating Number */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 + 0.3 }}
                      className={`absolute bottom-6 ${isRTL ? 'left-6' : 'right-6'} text-8xl font-bold text-primary/10`}
                    >
                      0{idx + 1}
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                      {value.title[isRTL ? 'ar' : 'en']}
                    </h3>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      {value.description[isRTL ? 'ar' : 'en']}
                    </p>

                    {/* Arrow Indicator */}
                    <div className={`absolute bottom-8 ${isRTL ? 'left-8' : 'right-8'} opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-2`}>
                      <DirectionalArrow className="h-8 w-8 text-primary" />
                    </div>
                  </div>

                  {/* Shine Effect */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                      isRTL
                        ? 'translate-x-full group-hover:-translate-x-full'
                        : '-translate-x-full group-hover:translate-x-full'
                    } transition-transform duration-1000`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CHAMPIONS ENTITIES ==================== */}
      <section className="py-32 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Crown className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('شركات تشامبيونز', 'The Champions Group')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('منظومة متكاملة من الكيانات', 'An Integrated Family of Entities')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'ضمن رؤية تكاملية لبناء منظومة رياضية متطورة ومستدامة، انبثقت عن الأكاديمية مجموعة من الكيانات المتخصصة تحت مظلة تشامبيونز',
                'Under an integrated vision for an advanced, sustainable sports system, a group of specialized entities emerged from the academy under the Champions umbrella'
              )}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {entities.map((entity, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                <div className="relative h-full bg-card border border-border rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex p-4 rounded-2xl bg-primary/10 border border-primary/20 mb-6"
                  >
                    <entity.icon className="h-8 w-8 text-primary" />
                  </motion.div>
                  <h3 className={`text-xl font-bold mb-4 group-hover:text-primary transition-colors ${isRTL ? 'text-right' : 'text-left'}`}>
                    {entity.title[isRTL ? 'ar' : 'en']}
                  </h3>
                  <p className={`text-muted-foreground leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                    {entity.description[isRTL ? 'ar' : 'en']}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== STRATEGIC PARTNERSHIPS ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(var(--accent-rgb),0.08)_0%,transparent_60%)]" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Globe className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('الشراكات الاستراتيجية', 'Strategic Partnerships')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('شركاء نحو العالمية', 'Partners Toward the World')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'شراكات نوعية رفعت جودة برامجنا التدريبية وفتحت آفاقًا عالمية أمام اللاعبين والكادر الفني',
                'Quality partnerships that have raised the standard of our programs and opened global horizons for our players and staff'
              )}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {partnerships.map((partner, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative"
              >
                <div className="relative h-full bg-card border border-border rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  <div className={`flex items-center gap-4 mb-4 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <div className="flex-shrink-0 p-3 rounded-xl bg-primary/10 border border-primary/20">
                      <Globe className="h-7 w-7 text-primary" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                        {partner.title[isRTL ? 'ar' : 'en']}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-sm text-primary font-medium">
                        <MapPin className="h-3.5 w-3.5" />
                        {partner.country[isRTL ? 'ar' : 'en']}
                      </span>
                    </div>
                  </div>
                  <p className={`text-muted-foreground leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                    {partner.description[isRTL ? 'ar' : 'en']}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ACHIEVEMENTS & RECOGNITION ==================== */}
      <section className="py-32 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Medal className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('الإنجازات والحوافز', 'Achievements & Recognition')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('بصمات على أرض الملعب', 'Marks Made on the Field')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'إنجازات رياضية متنوعة محليًا ودوليًا تعكس جودة العمل والتزام اللاعبين والكادر',
                'A range of local and international achievements that reflect the quality of our work and the commitment of our players and staff'
              )}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            {[
              { ar: 'بطولات كرة القدم على مستوى قطاع غزة', en: 'Football titles across the Gaza Strip' },
              { ar: 'ألقاب جماعية أبرزها في كرة السلة للفتيات', en: 'Team titles, most notably in girls\' basketball' },
              { ar: 'بطولات فردية في الكاراتيه وتنس الطاولة', en: 'Individual titles in karate and table tennis' },
              { ar: 'تمثيل فلسطين دوليًا في كرة القدم وتنس الطاولة والكاراتيه', en: 'Representing Palestine internationally in football, table tennis and karate' },
              { ar: 'لقب أفضل أكاديمية رياضية 2020 من المجلس الأعلى للشباب والرياضة', en: 'Best Sports Academy 2020 — Higher Council for Youth & Sports' },
              { ar: 'احتراف اللاعب يوسف الجمل في أوروبا', en: 'Player Yousef Al-Jamal turning professional in Europe' },
              { ar: 'معايشات تدريبية خارجية في تنس الطاولة والدبكة', en: 'External training camps in table tennis and dabke' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className={`flex items-start gap-4 p-5 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
              >
                <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10 border border-primary/20">
                  <Medal className="h-6 w-6 text-primary" />
                </div>
                <span className="text-foreground font-medium leading-relaxed pt-1">
                  {item[isRTL ? 'ar' : 'en']}
                </span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/achievements">
              <Button size="lg" variant="outline" className="group">
                <span className="flex items-center gap-2">
                  {navLabel('استعرض كل الإنجازات', 'Explore All Achievements')}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== METHODOLOGY / SUSTAINABLE DEVELOPMENT ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(var(--primary-rgb),0.08)_0%,transparent_60%)]" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('تطوير مستدام وفق معايير عالمية', 'Sustainable Development to Global Standards')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('علم وتقنية في خدمة البطل', 'Science & Technology in Service of the Champion')}
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`text-muted-foreground text-lg md:text-xl leading-relaxed mb-10 ${isRTL ? 'text-right' : 'text-left'}`}
            >
              {navLabel(
                'تتبنى تشامبيونز منهجية تدريبية حديثة قائمة على العلوم الرياضية والتحليل الرقمي، حيث وظّفت تقنيات تحليل الأداء بالفيديو (Play – Metrica Sports)، واستفادت من برامج مركز برشلونة للابتكار لتطوير الكادر الإداري والفني.',
                'Champions adopts a modern training methodology rooted in sports science and digital analysis. It employs video performance-analysis technology (Play – Metrica Sports) and draws on the Barcelona Innovation Center\'s programs to develop its administrative and technical staff.'
              )}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative p-8 md:p-10 rounded-3xl bg-card border border-primary/20 shadow-xl"
            >
              <Sparkles className={`absolute top-6 ${isRTL ? 'left-6' : 'right-6'} h-8 w-8 text-primary/30`} />
              <p className={`text-xl md:text-2xl font-bold leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                {navLabel(
                  'نؤمن بأن صناعة البطل لا تعتمد على الموهبة وحدها، بل على بيئة تدريبية متكاملة، ومنهجية علمية دقيقة، واستثمار طويل الأمد في بناء الإنسان قبل اللاعب.',
                  'We believe that making a champion does not depend on talent alone, but on an integrated training environment, a precise scientific methodology, and a long-term investment in building the person before the player.'
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== STATS COUNTER ==================== */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('بالأرقام', 'By The Numbers')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('إنجازاتنا تتحدث', 'Our Impact Speaks Volumes')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'أرقام حقيقية تعكس التزامنا المستمر بالتميز والتطوير وبناء مستقبل أفضل',
                'Real numbers reflecting our continuous commitment to excellence, development, and building a better future'
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-7xl mx-auto">
            {statsDisplay.map((stat, idx) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  type: 'spring',
                  stiffness: 100,
                  delay: idx * 0.1,
                }}
              >
                <motion.div
                  whileHover={{ y: -8, scale: 1.05 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />

                  <div className="relative bg-card border border-border rounded-2xl p-6 text-center shadow-lg hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 + 0.3 }}
                      className="text-4xl md:text-5xl font-bold mb-2 gradient-text"
                    >
                      {stat.value}
                    </motion.div>
                    <div className="text-sm text-muted-foreground">
                      {t(stat.labelKey)}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== LEADERSHIP PREVIEW ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel('قيادتنا', 'Our Leadership')}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel('قيادة بالشغف والخبرة', 'Led by Passion & Expertise')}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {navLabel(
                'فريق قيادي يجمع بين عقود من الخبرة الرياضية والتزام عميق بتطوير الشباب',
                'A leadership team combining decades of sports experience with deep commitment to youth development'
              )}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {[
              {
                image: '/assets/team/s-blob-v1-IMAGE-AdrJGHuqoHQ.jpeg',
                name: navLabel('فريق القيادة', 'Leadership Team'),
                role: navLabel('قيادة التميز', 'Driving Excellence'),
              },
              {
                image: '/assets/team/s-blob-v1-IMAGE-ErIcjKSeec4.jpeg',
                name: navLabel('مدربون خبراء', 'Expert Coaches'),
                role: navLabel('تطوير المواهب', 'Developing Talent'),
              },
              {
                image: '/assets/team/s-blob-v1-IMAGE-oNCoErmyHAo.jpeg',
                name: navLabel('طاقم الدعم', 'Support Staff'),
                role: navLabel('تمكين النجاح', 'Enabling Success'),
              },
            ].map((member, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                whileHover={{ y: -12 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />

                <div className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  <div className="relative h-96 overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

                    <div className={`absolute bottom-6 ${isRTL ? 'right-6 left-6' : 'left-6 right-6'} ${isRTL ? 'text-right' : 'text-left'}`}>
                      <h3 className="text-2xl font-bold mb-2 text-foreground">
                        {member.name}
                      </h3>
                      <p className="text-primary font-medium">{member.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA to Team Page */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/team">
              <Button size="lg" variant="outline" className="group">
                <span className="flex items-center gap-2">
                  {navLabel('تعرف على الفريق الكامل', 'Meet the Full Team')}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== CLOSING TAGLINE ==================== */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Flame className="h-10 w-10 text-primary mx-auto mb-8" />
            <p className="text-2xl md:text-3xl font-bold leading-relaxed gradient-text">
              {navLabel(
                'تشامبيونز… حكاية صمود، وفكرة تكافح التحديات بعزيمة وإصرار، تشق طريقًا احترافيًا لصناعة الأبطال، ومسارٌ متواصل يؤكد أن الرياضة قادرة على بناء المستقبل مهما اشتدت التحديات.',
                'Champions… a story of resilience — an idea that fights challenges with determination, carving a professional path to make champions, and a continuing journey that proves sport can build the future, however hard the challenges.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg"
            alt="Join Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/95" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/30 backdrop-blur-xl mb-8"
            >
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-sm font-bold text-primary">
                {navLabel('ابدأ رحلتك معنا', 'Start Your Journey With Us')}
              </span>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8">
              <span className="gradient-text">
                {navLabel('هل أنت جاهز لتكون بطلًا؟', 'Ready to Become a Champion?')}
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-muted-foreground mb-12 leading-relaxed">
              {navLabel(
                'انضم إلى آلاف الرياضيين الذين اختاروا أكاديمية تشامبيونز لبدء رحلتهم نحو التميز',
                'Join thousands of athletes who chose Champions Academy to start their journey towards excellence'
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" className="btn-glow group px-8">
                    <span className="flex items-center gap-2">
                      <Rocket className="h-5 w-5" />
                      {navLabel('احجز الآن', 'Book Now')}
                      <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Button>
                </motion.div>
              </Link>

              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" variant="outline" className="group px-8">
                    <span className="flex items-center gap-2">
                      {navLabel('تواصل معنا', 'Contact Us')}
                      <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Button>
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
